/**
 * Rechenzentrum vor Ort: Gekauftes wird hier in die Racks eingebaut.
 *
 * Jedes Rack zeigt seine Höheneinheiten von unten nach oben, dazu die Geräte,
 * die schon drin stehen, und die Einbauten, die gerade laufen. Im Lager des
 * Standorts liegt, was gekauft und noch nicht eingebaut ist. Der Einbau dauert
 * einige Stunden und lässt sich abbrechen.
 */
import { useState, type ComponentProps } from 'react';
import { Badge, Button, EmptyState, Input, Modal, PageHeader, Select } from '../components/ui';
import { DeviceArt, type ArtKind } from '../components/DeviceArt';
import { useGame } from '../store/game';
import { money, num } from '../lib/format';
import type { Delivery, Rack, World } from '@hosting-sim/core';

/** Auswahlfeld mit einer Optionsliste – übersetzt sie in die Kinder von `Select`. */
function Pick({ options, ...props }: Omit<ComponentProps<typeof Select>, 'children'> & { options: { value: string; label: string }[] }) {
  return (
    <Select {...props}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

/** Höhe einer Höheneinheit im Rackbild in Pixeln. */
const UNIT_PX = 9;

interface PlacedDevice {
  id: string;
  name: string;
  kind: ArtKind;
  rackUnit: number;
  heightUnits: number;
  maxWatt: number;
  pending: boolean;
}

/** Alle Geräte und laufenden Einbauten eines Racks, bereit für die Zeichnung. */
export function placedIn(world: World, rack: Rack): PlacedDevice[] {
  const devices: PlacedDevice[] = [];
  const add = (
    list: Record<string, { id: string; name: string; rackId: string | null; rackUnit: number; heightUnits: number; maxWatt: number }>,
    kind: ArtKind,
  ) => {
    for (const d of Object.values(list)) {
      if (d.rackId !== rack.id) continue;
      devices.push({ id: d.id, name: d.name, kind, rackUnit: d.rackUnit, heightUnits: d.heightUnits, maxWatt: d.maxWatt, pending: false });
    }
  };
  add(world.servers, 'server');
  add(world.routers, 'router');
  add(world.switches, 'switch');
  add(world.firewalls, 'firewall');
  add(world.loadBalancers, 'loadbalancer');
  add(world.upsUnits, 'ups');
  for (const job of Object.values(world.deliveries)) {
    if (!job.install || job.install.rackId !== rack.id) continue;
    devices.push({
      id: job.id,
      name: `${job.label} (Einbau)`,
      kind: job.kind === 'loadbalancer' ? 'loadbalancer' : job.kind,
      rackUnit: job.install.rackUnit,
      heightUnits: job.heightUnits,
      maxWatt: job.maxWatt,
      pending: true,
    });
  }
  return devices;
}

export function VorOrt() {
  const world = useGame((s) => s.world);
  if (!world) return null;

  const datacenters = Object.values(world.datacenters);
  const totalStored = Object.values(world.deliveries).filter((d) => !d.install).length;
  const totalInstalling = Object.values(world.deliveries).filter((d) => d.install).length;

  return (
    <>
      <PageHeader
        title="Rechenzentrum vor Ort"
        description={`Im Lager liegen ${num(totalStored)} Geräte, ${num(totalInstalling)} werden gerade eingebaut.`}
      />

      {datacenters.length === 0 && <EmptyState title="Kein Standort" description="Zuerst einen Standort eröffnen." />}

      <div className="space-y-8">
        {datacenters.map((dc) => {
          const racks = Object.values(world.racks).filter((r) => r.datacenterId === dc.id);
          const stored = Object.values(world.deliveries).filter((d) => d.datacenterId === dc.id && !d.install);
          const installing = Object.values(world.deliveries).filter((d) => d.datacenterId === dc.id && d.install);
          return (
            <section key={dc.id} className="space-y-4">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-lg font-semibold">{dc.city}</h2>
                <span className="text-xs text-slate-500">
                  {racks.length} Rack(s) · {num(stored.length)} im Lager
                </span>
              </div>

              {racks.length === 0 ? (
                <EmptyState title="Keine Racks" description="In diesem Standort ist noch kein Rack gemietet." />
              ) : (
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {racks.map((rack) => (
                    <RackView key={rack.id} world={world} rack={rack} />
                  ))}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <StorageList world={world} items={stored} />
                <InstallingList world={world} items={installing} />
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Rack
// ---------------------------------------------------------------------------

function RackView({ world, rack }: { world: World; rack: Rack }) {
  const devices = placedIn(world, rack);
  const usedUnits = devices.reduce((sum, d) => sum + d.heightUnits, 0);
  const usedKw = devices.filter((d) => !d.pending).reduce((sum, d) => sum + d.maxWatt / 1000, 0);

  return (
    <div className="w-44 shrink-0">
      <div className="mb-2 flex items-baseline justify-between text-xs">
        <span className="font-semibold text-slate-200">{rack.name}</span>
        <span className="text-slate-500">
          {usedUnits}/{rack.units} HE · {usedKw.toFixed(1)}/{rack.powerBudgetKw} kW
        </span>
      </div>
      <div
        className="relative rounded-md border-2 border-slate-600 bg-slate-950 p-1"
        style={{ height: rack.units * UNIT_PX + 8 }}
        aria-label={`${rack.name}: ${usedUnits} von ${rack.units} Höheneinheiten belegt`}
      >
        {devices.map((d) => {
          const bottom = d.rackUnit * UNIT_PX;
          const height = d.heightUnits * UNIT_PX - 2;
          return (
            <div
              key={d.id}
              title={d.name}
              className={`absolute inset-x-1 overflow-hidden rounded-sm text-[9px] leading-none text-white ${
                d.pending
                  ? 'border border-dashed border-amber-300 bg-[repeating-linear-gradient(45deg,rgba(251,191,36,0.35)_0_4px,transparent_4px_8px)]'
                  : 'bg-slate-700'
              }`}
              style={{ bottom: bottom + 4, height }}
            >
              <DeviceArt kind={d.kind} heightUnits={d.heightUnits} className="h-full w-full opacity-70" />
            </div>
          );
        })}
      </div>
      <div className="mt-1 text-[10px] text-slate-500">Platz 1 ist unten.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lager und laufende Einbauten
// ---------------------------------------------------------------------------

function StorageList({ world, items }: { world: World; items: Delivery[] }) {
  const [chosen, setChosen] = useState<Delivery | null>(null);
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3">
      <div className="mb-2 text-sm font-semibold">Lager</div>
      {items.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-500">Nichts im Lager.</div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="flex items-center gap-3">
              <div className="h-12 w-16 shrink-0 rounded bg-slate-950/60 p-1">
                <DeviceArt kind={d.kind} heightUnits={d.heightUnits} className="h-full w-full" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{d.label}</div>
                <div className="text-xs text-slate-500">{d.heightUnits} HE · {money(d.price)} bezahlt</div>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setChosen(d)}>
                Einbauen
              </Button>
            </div>
          ))}
        </div>
      )}
      {chosen && <InstallDialog world={world} delivery={chosen} onClose={() => setChosen(null)} />}
    </div>
  );
}

function InstallingList({ world, items }: { world: World; items: Delivery[] }) {
  const run = useGame((s) => s.run);
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3">
      <div className="mb-2 text-sm font-semibold">Im Einbau</div>
      {items.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-500">Gerade wird nichts eingebaut.</div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => {
            const rack = world.racks[d.install!.rackId];
            const hours = Math.max(0, d.install!.doneTick - world.meta.tick);
            return (
              <div key={d.id} className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">{d.label}</div>
                  <div className="text-xs text-slate-500">
                    {rack?.name} · Platz {d.install!.rackUnit + 1} · fertig in {hours} Std.
                  </div>
                </div>
                <Badge tone="info">Einbau</Badge>
                <Button size="sm" variant="ghost" onClick={() => void run({ type: 'dc/cancelInstall', deliveryId: d.id })}>
                  Abbrechen
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InstallDialog({ world, delivery, onClose }: { world: World; delivery: Delivery; onClose: () => void }) {
  const run = useGame((s) => s.run);
  const racks = Object.values(world.racks).filter((r) => r.datacenterId === delivery.datacenterId);
  const [rackId, setRackId] = useState<string>(racks[0]?.id ?? '');
  const [manual, setManual] = useState(false);
  const [slot, setSlot] = useState('1');
  const [busy, setBusy] = useState(false);

  const rack = racks.find((r) => r.id === rackId);
  const slotNumber = Number(slot);
  const slotValid = !manual || (Number.isInteger(slotNumber) && slotNumber >= 1 && rack !== undefined && slotNumber + delivery.heightUnits - 1 <= rack.units);

  async function install() {
    setBusy(true);
    try {
      const result = await run({
        type: 'dc/installDelivery',
        deliveryId: delivery.id,
        rackId: rackId as never,
        rackUnit: manual ? slotNumber - 1 : undefined,
      });
      if (result.ok) onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${delivery.label} einbauen`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} disabled={!rack || !slotValid} onClick={install}>
            Einbau starten
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex justify-center rounded-lg bg-slate-950/60 p-4">
          <DeviceArt kind={delivery.kind} heightUnits={delivery.heightUnits} className="h-28 w-full max-w-xs" />
        </div>
        {racks.length === 0 ? (
          <p className="text-sm text-amber-400">In diesem Standort gibt es kein Rack.</p>
        ) : (
          <Pick
            label="Rack"
            value={rackId}
            onChange={(e) => setRackId(e.target.value)}
            options={racks.map((r) => ({ value: r.id, label: `${r.name} (${r.units} HE)` }))}
          />
        )}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} />
          Platz selbst wählen (sonst der erste freie)
        </label>
        {manual && (
          <Input
            label="Platz von unten (1 = unten)"
            type="number"
            min={1}
            max={rack ? rack.units - delivery.heightUnits + 1 : 1}
            value={slot}
            onChange={(e) => setSlot(e.target.value)}
            hint={slotValid ? `belegt ${delivery.heightUnits} HE` : 'Platz passt nicht ins Rack'}
          />
        )}
        <p className="text-xs text-slate-400">
          Der Einbau dauert einige Stunden. Während dieser Zeit ist der Platz reserviert.
        </p>
      </div>
    </Modal>
  );
}
